import { ScrollView, View } from 'react-native';
import TextLato from '../../../text/text-lato/TextLato';
import { ICON_NAMES } from '../../../icon/icon.helper';
import { brand } from 'src/theme/colors';
import { MasonryGrid } from '../../../masonry-grid/MasonryGrid';
import { MasonryItem } from '../../../masonry-grid/MasonryItem';
import { Dropdown } from '../../../dropdown/Dropdown';
import { PhotoCard } from '../../../photo-card/PhotoCard';
import { PrimaryButton } from '../../../buttons/primary-button/PrimaryButton';
import { TestIDs } from 'src/constants/testIDs.constants';
import { drawerTestID, modalTestID } from 'src/helpers/testIDs.helper';
import { FocusGroups } from '../../../focus-provider/FocusProvider.constants';
import { PhotoDrawerSortingOptions } from '../../DrawerPhotoDrawer.types';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents, AnalyticsSelectionTypes } from 'src/_shared/analyticsEvents';
import { TextIconButton } from '../../../buttons/text-icon-button/TextIconButton';
import {
  TEXT_ICON_BUTTON_SIZES,
  TEXT_ICON_BUTTON_TYPES,
} from '../../../buttons/text-icon-button/TextIconButton.constants';
import { sourcesDesktopTopNew } from '../../../modals/dynamic-modals/modal-upload-multisource/ModalUploadMultisource.constants';
import { PickerSourceButton } from '../../../buttons/picker-source-button/PickerSourceButton';
import { Icon } from '../../../icon/Icon';
import { Overlay } from 'src/theme-v2/overlay';
import { Stroke } from 'src/theme-v2/stroke';
import { useStore } from 'src/contexts/StoreContext';
import { observer } from 'mobx-react-lite';
import { style } from './PickerComputer.style';
import { SELECTED_MENU_ITEMS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { SECTION_NAMES } from 'src/constants/sectionNames.contants';

export const PickerComputer = observer(
  ({
    activeProject,
    dropdownOptions,
    sendAmplitudeEvents,
    dropdownSelectedOption,
    PhotoDrawerSortingOptionsLabels,
    setDropDownSelectedOption,
    onPressSourceButton,
    selectedThumbnails,
    ctaLabel,
    isActiveDelete,
    onPressDelete,
    scrollOffsetRef,
    sortedImages,
    wrapperRef,
    handleMouseDown,
    handleMouseMove,
    selection,
    layoutMap,
    onDragToGrid,
    onPressCard,
    images,
  }: any) => {
    const { i18n, project, drawer } = useStore();

    return (
      <>
        <View style={style.functionBar}>
          <TextLato style={style.text}>{i18n.t('photo-drawer:order-by')}</TextLato>
          {/* DROPDOWN PLACEHOLDER */}
          <View style={{ width: '100%', height: 37 }} />
        </View>
        <View style={style.dropdown}>
          <Dropdown
            options={dropdownOptions}
            selectedOption={PhotoDrawerSortingOptionsLabels[dropdownSelectedOption]}
            onPressOption={(option: string) => {
              switch (option) {
                case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.SHOT_MOST_RECENT]:
                  sendAmplitudeEvents('SHOT_MOST_RECENT');
                  setDropDownSelectedOption(PhotoDrawerSortingOptions.SHOT_MOST_RECENT);
                  break;
                case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.SHOT_LESS_RECENT]:
                  sendAmplitudeEvents('SHOT_LESS_RECENT');
                  setDropDownSelectedOption(PhotoDrawerSortingOptions.SHOT_LESS_RECENT);
                  break;
                case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT]:
                  sendAmplitudeEvents('UPLOAD_MOST_RECENT');
                  setDropDownSelectedOption(PhotoDrawerSortingOptions.UPLOAD_MOST_RECENT);
                  break;
                case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.UPLOAD_LESS_RECENT]:
                  sendAmplitudeEvents('UPLOAD_LESS_RECENT');
                  setDropDownSelectedOption(PhotoDrawerSortingOptions.UPLOAD_LESS_RECENT);
                  break;
                case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.NAME_ALPHABETIC]:
                  sendAmplitudeEvents('NAME_ALPHABETIC');
                  setDropDownSelectedOption(PhotoDrawerSortingOptions.NAME_ALPHABETIC);
                  break;
                case PhotoDrawerSortingOptionsLabels[PhotoDrawerSortingOptions.NAME_ALPHABETIC_REVERSE]:
                  sendAmplitudeEvents('NAME_ALPHABETIC_REVERSE');
                  setDropDownSelectedOption(PhotoDrawerSortingOptions.NAME_ALPHABETIC_REVERSE);
                  break;
              }
            }}
          />
        </View>
        <View style={{ paddingHorizontal: 32, paddingVertical: 16, gap: 12 }}>
          <TextLato style={style.text}>{i18n.t('photo-drawer:upload-from')}</TextLato>
          <View style={style.sourcesWrapper}>
            {sourcesDesktopTopNew.map((source, index) => (
              <PickerSourceButton
                style={style.pickerButton}
                hoverStyle={style.pickerButtonHover}
                pickerStyle={{ color: brand.main.water }}
                key={source}
                source={source}
                isSelected={false}
                onPress={async () => {
                  onPressSourceButton(source);
                }}
                text={i18n.t(`upload:${source}`)}
                focusId={modalTestID(`choose-${source}`)}
                focusGroup={FocusGroups.MODAL}
                focusOrder={index}
              />
            ))}
          </View>
        </View>
        <View style={style.cta}>
          <TextIconButton
            focusGroup={FocusGroups.MODAL}
            focusId={selectedThumbnails ? modalTestID(TestIDs.DESELECT_ALL) : modalTestID(TestIDs.SELECT_ALL)}
            testID={selectedThumbnails ? modalTestID(TestIDs.DESELECT_ALL) : modalTestID(TestIDs.SELECT_ALL)}
            focusOrder={10}
            text={ctaLabel}
            onPress={async () => {
              if (selectedThumbnails > 0) {
                project.activeProject?.deselectAllThumbnailImages();
                amplitude.trackEvent(AnalyticsEvents.personalize.photoDrawerSelectPhotos, {
                  type: AnalyticsSelectionTypes.deselectAll,
                });
              } else {
                project.activeProject?.selectAllThumbnailImages();
                amplitude.trackEvent(AnalyticsEvents.personalize.photoDrawerSelectPhotos, {
                  type: AnalyticsSelectionTypes.selectAll,
                });
              }
            }}
            size={TEXT_ICON_BUTTON_SIZES.SMALL}
            type={TEXT_ICON_BUTTON_TYPES.NO_ICON}
            iconName={ICON_NAMES.AddFrame}
            textStyle={style.textLink}
            accessible
            accessibilityRole="button"
            accessibilityLabel={ctaLabel}
          />
          {isActiveDelete && (
            <>
              <View style={{ height: 14, width: 1, backgroundColor: brand.main.water }} />
              <TextIconButton
                focusGroup={FocusGroups.MODAL}
                focusId={modalTestID(TestIDs.DELETE_SELECTED)}
                testID={modalTestID(TestIDs.DELETE_SELECTED)}
                focusOrder={10}
                text={i18n.t('group-action-bar:delete-selected')}
                onPress={async () => onPressDelete()}
                size={TEXT_ICON_BUTTON_SIZES.SMALL}
                type={TEXT_ICON_BUTTON_TYPES.NO_ICON}
                iconName={ICON_NAMES.AddFrame}
                textStyle={style.textLink}
                accessible
                accessibilityRole="button"
                accessibilityLabel={i18n.t('group-action-bar:delete-selected')}
              />
            </>
          )}
        </View>
        <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          onScroll={e => {
            scrollOffsetRef.current = e.nativeEvent.contentOffset.y;
          }}
          scrollEventThrottle={16}
        >
          {sortedImages && sortedImages?.length > 0 ? (
            <View
              ref={wrapperRef}
              onPointerDown={handleMouseDown}
              onPointerMove={handleMouseMove}
              style={{ position: 'relative' }}
            >
              <MasonryGrid itemHeight={170} gap={6} outerGap={32}>
                {sortedImages?.map((image, index) => {
                  return (
                    <MasonryItem
                      width={image.width}
                      height={image.height}
                      key={`grid-item-${index}`}
                      onLayout={e => (layoutMap.current[index] = e.nativeEvent.layout)}
                    >
                      <View ref={(node: any) => onDragToGrid(node, image)} style={{ width: '100%', height: '100%' }}>
                        <PhotoCard
                          image={image}
                          focusId={drawerTestID(TestIDs.SELECT_PHOTO, index.toString())}
                          focusGroup={FocusGroups.DRAWER}
                          focusOrder={index + 3}
                          testID={drawerTestID(TestIDs.SELECT_PHOTO, index.toString())}
                          onPress={() => onPressCard(image, index)}
                        />
                      </View>
                    </MasonryItem>
                  );
                })}
              </MasonryGrid>
              {selection?.isSelecting && wrapperRef.current && (
                <View
                  style={{
                    position: 'absolute',
                    left: Math.min(selection.startX, selection.endX),
                    top: Math.min(selection.startY, selection.endY) + scrollOffsetRef.current - wrapperRef.current?.y,
                    width: Math.abs(selection.startX - selection.endX),
                    height: Math.abs(selection.startY - selection.endY),
                    backgroundColor: Overlay.OverlayColorTileSelected,
                    borderColor: Stroke.StrokeColor.StrokeColorBrand,
                    borderWidth: Stroke.StrokeThickness.StrokeThicknessDefault,
                    pointerEvents: 'none',
                    zIndex: 9999,
                  }}
                />
              )}
            </View>
          ) : (
            <View style={style.emptyStateContainer}>
              <Icon name={ICON_NAMES.PhotoAlbum} height={41} width={45} color={brand.main.water} />
              <TextLato style={style.emptyStateText}>{i18n.t('photo-drawer:empty-text')}</TextLato>
            </View>
          )}
        </ScrollView>
        {project.activeProject!.selectedThumbnails.length > 0 && (
          <View style={style.footer}>
            <PrimaryButton
              text={i18n.t('photo-drawer:add-n-photos', {
                number: project.activeProject?.selectedThumbnails.length,
              })}
              testID={drawerTestID(TestIDs.ADD_TO_PROJECT)}
              focusId={drawerTestID(TestIDs.ADD_TO_PROJECT)}
              focusOrder={(images?.length ?? 0) + 3}
              focusGroup={FocusGroups.DRAWER}
              onPress={async () => {
                const googlePhotoCounter =
                  project.activeProject?.selectedThumbnails.filter(
                    thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.GooglePhoto,
                  )?.length ?? 0;
                const localPhotoCounter =
                  project.activeProject?.selectedThumbnails.filter(
                    thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.Computer,
                  )?.length ?? 0;
                const smartphonePhotoCounter =
                  project.activeProject?.selectedThumbnails.filter(
                    thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.Smartphone,
                  )?.length ?? 0;
                const familyFriendsPhotoCounter =
                  project.activeProject?.selectedThumbnails.filter(
                    thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.FamilyFriends,
                  )?.length ?? 0;
                amplitude.trackEvent(AnalyticsEvents.dynamicPicker.continueClick, {
                  n_photo: project.activeProject!.selectedThumbnails.length,
                  n_photos_facebook: 0,
                  n_photos_gphoto: googlePhotoCounter,
                  n_photos_local: localPhotoCounter,
                  n_photos_smartphone: smartphonePhotoCounter,
                  n_photos_family_friends: familyFriendsPhotoCounter,
                  section_name: SECTION_NAMES.DRAWER,
                });
                drawer.uploadButtonCallback();
              }}
              accessibilityLabel={i18n.t('photo-drawer:add-n-photos', {
                number: project.activeProject?.selectedThumbnails.length,
              })}
            />
          </View>
        )}
      </>
    );
  },
);

PickerComputer.displayName = 'PickerComputer';
